/** Client preview plan only. The server validates and commits the final batch. */
import { Schemas } from "@kollectivia/shared";

import { CHAMPS_PARTICIPANT } from "./champs";

export type WorkbookValues = Record<string, unknown>;
export type ExistingEmployee = WorkbookValues & { readonly client_row_id: string };
export type WorkbookColumn = {
  readonly index: number;
  readonly label: string;
  readonly suggested_field: string | null;
};
export type WorkbookSelection = {
  sheet_name: string;
  header_row: number;
  columns: Array<{ index: number; field: string }>;
};
export type InspectedWorkbook = {
  sheets: string[];
  selected_sheet: string | null;
  header_row: number;
  columns: WorkbookColumn[];
};
export type PreviewRow = {
  ligne: number;
  values: WorkbookValues;
  issues: Array<{ field: string | null; reason: string }>;
};
export type PreviewWorkbook = {
  rows: PreviewRow[];
  rows_detected: number;
  source_file_id: string;
  source_selection: WorkbookSelection;
  revision: string;
};
export type WorkbookEntry = {
  key: string;
  sourceLine: number;
  action: "add" | "update" | "skip";
  addId: string;
  targetId: string | null;
  values: WorkbookValues;
  /** An explicit edit, including an intentional clear, differs from a blank XLSX cell. */
  editedFields: ReadonlySet<string>;
  /** Local editing text; never serialized as part of participant values. */
  numericDrafts?: Readonly<Record<string, string>>;
  issues: PreviewRow["issues"];
  ambiguous: boolean;
};

const isBlank = (value: unknown) => value === null || value === undefined || value === "";
function rawCells(values: WorkbookValues): Record<string, string> {
  return (values.import_raw ?? {}) as Record<string, string>;
}

/** Exact unique business-key matches are proposed for review, never committed here. */
export function seedWorkbookEntries(
  rows: readonly PreviewRow[],
  existing: readonly ExistingEmployee[],
  makeId = () => crypto.randomUUID(),
): WorkbookEntry[] {
  const byNumber = new Map<string, ExistingEmployee[]>();
  for (const row of existing) {
    if (isBlank(row.employee_number)) continue;
    const key = String(row.employee_number);
    byNumber.set(key, [...(byNumber.get(key) ?? []), row]);
  }
  const sourceCounts = new Map<string, number>();
  for (const row of rows) {
    if (isBlank(row.values.employee_number)) continue;
    const key = String(row.values.employee_number);
    sourceCounts.set(key, (sourceCounts.get(key) ?? 0) + 1);
  }
  return rows.map((row) => {
    const number = isBlank(row.values.employee_number) ? null : String(row.values.employee_number);
    const matches = number === null ? [] : (byNumber.get(number) ?? []);
    const ambiguous =
      matches.length > 1 || (number !== null && (sourceCounts.get(number) ?? 0) > 1);
    return {
      key: `source-${row.ligne}`,
      sourceLine: row.ligne,
      addId: makeId(),
      targetId: matches.length === 1 ? matches[0]!.client_row_id : null,
      action: ambiguous ? "skip" : matches.length === 1 ? "update" : "add",
      values: {
        ...row.values,
        ...(row.values.import_raw ? { import_raw: { ...rawCells(row.values) } } : {}),
      },
      editedFields: new Set<string>(),
      issues: row.issues,
      ambiguous,
    };
  });
}

/** Blank cells preserve existing values on updates; clearing requires a deliberate edit. */
export function workbookPatchValues(entry: WorkbookEntry): WorkbookValues {
  const output: WorkbookValues = {};
  const raw = { ...rawCells(entry.values) };
  const values = { ...entry.values };
  for (const [field, text] of Object.entries(entry.numericDrafts ?? {})) {
    const blank = text.trim() === "";
    const number = blank ? null : Number(text.replace(",", "."));
    const input = blank || Number.isFinite(number) ? number : text;
    const prepared = Schemas.prepareCensusImportedValues({ [field]: input });
    Object.assign(values, prepared);
    delete raw[field];
    Object.assign(raw, rawCells(prepared));
  }
  for (const [key, value] of Object.entries(values)) {
    if (key === "import_raw") continue;
    if (
      entry.action === "update" &&
      isBlank(value) &&
      !entry.editedFields.has(key) &&
      !(key in raw)
    )
      continue;
    output[key] = value;
  }
  if (Object.keys(raw).length) output.import_raw = { ...raw };
  return output;
}

/** Shared validators preserve unreadable typed cells; no second business-rule engine. */
export function editWorkbookField(
  entry: WorkbookEntry,
  field: string,
  text: string,
): WorkbookEntry {
  const numeric = CHAMPS_PARTICIPANT.find((champ) => champ.cle === field)?.type === "number";
  if (numeric) {
    return {
      ...entry,
      numericDrafts: { ...entry.numericDrafts, [field]: text },
      editedFields: new Set([...entry.editedFields, field]),
    };
  }
  const prepared = Schemas.prepareCensusImportedValues({ [field]: text });
  const raw = { ...rawCells(entry.values) };
  delete raw[field];
  Object.assign(raw, rawCells(prepared));
  const values = { ...entry.values, ...prepared };
  if (Object.keys(raw).length) values.import_raw = raw;
  else delete values.import_raw;
  return { ...entry, values, editedFields: new Set([...entry.editedFields, field]) };
}

export function workbookEffectiveValues(
  entry: WorkbookEntry,
  existing: readonly ExistingEmployee[],
): WorkbookValues {
  const previous =
    entry.action === "update"
      ? existing.find((row) => row.client_row_id === entry.targetId)
      : undefined;
  const patch = workbookPatchValues(entry);
  const values = { ...(previous ?? {}), ...patch };
  const incomingRaw = rawCells(patch);
  const raw = { ...rawCells(previous ?? {}), ...incomingRaw };
  for (const key of Object.keys(patch))
    if (key !== "import_raw" && !(key in incomingRaw)) delete raw[key];
  if (Object.keys(raw).length) values.import_raw = raw;
  else delete values.import_raw;
  return values;
}

export function workbookDisplayValues(
  entry: WorkbookEntry,
  existing: readonly ExistingEmployee[],
): Record<string, string> {
  const values = workbookEffectiveValues(entry, existing);
  const raw = rawCells(values);
  return {
    ...Object.fromEntries(
      Object.entries(values)
        .filter(([key]) => key !== "import_raw")
        .map(([key, value]) => [
          key,
          isBlank(value) && key in raw
            ? raw[key]!
            : value === null || value === undefined
              ? ""
              : String(value),
        ]),
    ),
    ...entry.numericDrafts,
  };
}

export function workbookOperations(entries: readonly WorkbookEntry[]) {
  return entries
    .filter((entry) => entry.action !== "skip")
    .map((entry) => {
      if (entry.action === "update" && !entry.targetId) throw new Error("WORKBOOK_TARGET_REQUIRED");
      return {
        operation: entry.action as "add" | "update",
        client_row_id: entry.action === "update" ? entry.targetId! : entry.addId,
        values: workbookPatchValues(entry),
      };
    });
}
